import { match } from 'ts-pattern'
import type {
  BeaconEntityKind,
  BeaconSummary,
  CoverageStatus,
  DismissedLead,
  EntityCoverage,
} from '../../../utils/api-client/api-schemas.ts'
import { DETECTION_LENSES } from '../beacon-run.ts'

/**
 * A finished run's coverage, read from its stored summary (§3) alone — no event replay — and indexed
 * for the coverage views: every part of the attack surface with its status, effort and verified findings.
 */

/** The matrix columns: the six detection lenses, then verification, which is a stage and not a lens. */
export const COVERAGE_COLUMNS = [...DETECTION_LENSES, 'ai_scan_verify'] as const

/** The coverage views name each lens by what it checks; the Run view keeps the lens names. */
const CHECK_LABELS: Record<string, string> = {
  ai_scan_surface_map: 'Mapped what callers can do',
  ai_scan_access: 'Access control',
  ai_scan_input_forward: 'Untrusted input',
  ai_scan_sink_backward: 'Dangerous calls',
  ai_scan_control_audit: 'Security controls',
  ai_scan_hygiene: 'Repo-wide hygiene',
  ai_scan_verify: 'Verification',
}

export const checkLabel = (lens: string): string =>
  CHECK_LABELS[lens] ?? lens.replace(/^ai_scan_/, '').replace(/_/g, ' ')

export const STATUS_LABELS: Record<CoverageStatus, string> = {
  examined: 'Examined',
  partly: 'Partly examined',
  not_examined: 'Not examined',
  not_applicable: 'Not applicable',
}

export type CoverageEntityKind = 'ep' | 'grp' | 'op' | 'ctl' | 'asset' | 'repo'

export type CoverageEntity = {
  key: string
  kind: CoverageEntityKind
  id: string
  name: string
  status: CoverageStatus
  reason: string
  lenses: string[]
  tokens: number
  tokensByLens: Record<string, number>
  files: number
  isApproximate: boolean
  /** Verified findings placed on this part or anything inside it. */
  findings: string[]
  /** Verified findings placed on this part itself, not inside it. */
  ownFindings: string[]
  /** Leads Beacon dismissed that would have been placed on this part or anything inside it. */
  dismissed: DismissedLead[]
}

export type OperationEntity = CoverageEntity & {
  kind: 'op'
  group: string
  groupKey: string
  entryPointKey: string
  caller: string
  effect: string
  location: string
}

export type GroupEntity = CoverageEntity & { kind: 'grp'; entryPointKey: string; operationKeys: string[] }

export type EntryPointEntity = CoverageEntity & {
  kind: 'ep'
  entryKind: string
  groupKeys: string[]
  operationKeys: string[]
}

export type ControlEntity = CoverageEntity & { kind: 'ctl'; isAuditable: boolean; controlKind: string }

export type AssetEntity = CoverageEntity & { kind: 'asset'; assetKind: string; operationKeys: string[] }

export type CoverageModel = {
  summary: BeaconSummary
  entities: ReadonlyMap<string, CoverageEntity>
  entryPoints: EntryPointEntity[]
  groups: ReadonlyMap<string, GroupEntity>
  operations: ReadonlyMap<string, OperationEntity>
  controls: ControlEntity[]
  assets: AssetEntity[]
  repository: CoverageEntity
  /** The sink-backward lens had nothing to trace, so its column and chips read "did not run". */
  isSinkLensUnrun: boolean
}

/** The model key of the part a summary entity reference names, as placements and dismissed leads give it. */
export const entityKeyOf = (kind: BeaconEntityKind, id: string): string =>
  match(kind)
    .with('operation', () => `op:${id}`)
    .with('group', () => `grp:${id}`)
    .with('entry_point', () => `ep:${id}`)
    .with('control', () => `ctl:${id}`)
    .with('repository', () => 'repo')
    .exhaustive()

/** The model key of the part a dismissed lead would have been placed on. */
export const dismissedLeadKey = (lead: Pick<DismissedLead, 'entity_kind' | 'entity_id'>): string =>
  entityKeyOf(lead.entity_kind, lead.entity_id)

const noLeads = (): DismissedLead[] => []

const base = (kind: CoverageEntityKind, key: string, entity: EntityCoverage | (EntityCoverage & object)) => ({
  key,
  kind,
  id: entity.id,
  name: entity.name,
  status: entity.status,
  reason: entity.reason,
  lenses: entity.lenses_run,
  tokens: entity.tokens_spent,
  tokensByLens: entity.tokens_by_lens,
  files: entity.files_read,
  isApproximate: entity.approximate ?? false,
  findings: entity.findings,
  dismissed: noLeads(),
})

/**
 * Leads the run dismissed that its summary does not list. A summary stored before Beacon recorded each
 * dismissed lead has the count but no list.
 */
export const unlistedDismissedCount = (summary: Pick<BeaconSummary, 'counts' | 'dismissed'>): number =>
  Math.max(0, summary.counts.false_positive - summary.dismissed.length)

/** Builds the model the coverage views read, from the summary alone. */
export const buildCoverageModel = (summary: BeaconSummary): CoverageModel => {
  const placedOn = new Map<string, string[]>()
  for (const placement of summary.finding_placements) {
    const key = `${placement.entity_kind}:${placement.entity_id}`
    placedOn.set(key, [...(placedOn.get(key) ?? []), placement.finding_uuid])
  }
  const own = (kind: string, id: string) => placedOn.get(`${kind}:${id}`) ?? []

  const operations = new Map<string, OperationEntity>()
  const groups = new Map<string, GroupEntity>()
  const entryPoints = summary.coverage.entry_points.map((ep): EntryPointEntity => {
    const entryPointKey = `ep:${ep.id}`
    const groupKeyOf = (name: string) => `grp:${ep.id}::${name}`
    for (const operation of ep.operations) {
      operations.set(`op:${operation.id}`, {
        ...base('op', `op:${operation.id}`, operation),
        kind: 'op',
        ownFindings: own('operation', operation.id),
        group: operation.group,
        groupKey: groupKeyOf(operation.group),
        entryPointKey,
        caller: operation.caller,
        effect: operation.effect,
        location: operation.location,
      })
    }
    for (const group of ep.groups) {
      const key = `grp:${group.id}`
      groups.set(key, {
        ...base('grp', key, { ...group, kind: 'group', reason: '', lenses_run: [] }),
        kind: 'grp',
        lenses: [...new Set(group.operation_ids.flatMap(id => operations.get(`op:${id}`)?.lenses ?? []))],
        ownFindings: own('group', group.id),
        entryPointKey,
        operationKeys: group.operation_ids.map(id => `op:${id}`),
      })
    }
    return {
      ...base('ep', entryPointKey, ep),
      kind: 'ep',
      ownFindings: own('entry_point', ep.id),
      entryKind: ep.kind,
      groupKeys: ep.groups.map(group => `grp:${group.id}`),
      operationKeys: ep.operations.map(operation => `op:${operation.id}`),
    }
  })

  const controls = summary.coverage.controls.map((control): ControlEntity => ({
    ...base('ctl', `ctl:${control.id}`, control),
    kind: 'ctl',
    ownFindings: own('control', control.id),
    isAuditable: control.auditable,
    controlKind: control.kind,
  }))
  const assets = summary.coverage.assets.map((asset): AssetEntity => ({
    ...base('asset', `asset:${asset.id}`, asset),
    kind: 'asset',
    ownFindings: [],
    assetKind: asset.kind,
    operationKeys: (asset.operation_ids ?? []).map(id => `op:${id}`),
  }))
  const repository: CoverageEntity = {
    ...base('repo', 'repo', summary.coverage.repository),
    ownFindings: own('repository', summary.coverage.repository.id),
  }

  const entities = new Map<string, CoverageEntity>([
    ...entryPoints.map(each => [each.key, each] as const),
    ...groups,
    ...operations,
    ...controls.map(each => [each.key, each] as const),
    ...assets.map(each => [each.key, each] as const),
    ['repo', repository],
  ])

  const enclosingKeys = (key: string): string[] => {
    const operation = operations.get(key)
    const group = groups.get(key)
    return operation ? [key, operation.groupKey, operation.entryPointKey] : group ? [key, group.entryPointKey] : [key]
  }
  for (const lead of summary.dismissed) {
    const key = entities.has(dismissedLeadKey(lead)) ? dismissedLeadKey(lead) : repository.key
    enclosingKeys(key).forEach(each => entities.get(each)?.dismissed.push(lead))
  }

  const sinkLens = summary.coverage.proof.lenses.find(lens => lens.lens === 'ai_scan_sink_backward')
  return {
    summary,
    entities,
    entryPoints,
    groups,
    operations,
    controls,
    assets,
    repository,
    isSinkLensUnrun:
      (sinkLens?.planned ?? 0) === 0 ||
      summary.coverage.proof.alarms.some(alarm => alarm.kind === 'sink_inventory_unavailable'),
  }
}

/** How deeply an operation was examined, 1–4 by the run's token quartiles; 0 when it was not. */
export const depthOf = (model: CoverageModel, operation: CoverageEntity): number =>
  operation.status !== 'examined'
    ? 0
    : 1 + model.summary.depth_thresholds.filter(threshold => operation.tokens > threshold).length

export const KIND_LABELS: Record<CoverageEntityKind, string> = {
  ep: 'Entry point',
  grp: 'Operation group',
  op: 'Operation',
  ctl: 'Security control',
  asset: 'Sensitive asset',
  repo: 'Repository',
}

export const formatTokens = (tokens: number): string =>
  match(tokens)
    .when(
      count => count >= 1e7,
      count => `${(count / 1e6).toFixed(1)}M`
    )
    .when(
      count => count >= 1e6,
      count => `${(count / 1e6).toFixed(2)}M`
    )
    .when(
      count => count >= 1e3,
      count => `${Math.round(count / 1e3)}k`
    )
    .otherwise(count => `${Math.round(count)}`)
